// PickupScreen.jsx

import React, {
    useMemo,
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { useSelector } from "react-redux";
import {
    Button,
    Container,
    MenuItem,
    Select,
    Stack,
    Typography,
    CircularProgress,
    TextField,
    InputAdornment,
    Alert,
    AlertTitle,
    Tooltip,
} from "@mui/material";
import EChartsReact from "echarts-for-react";
import DateRangePicker from "../components/DateRangePicker";
import ToggleButtonTitled from "../components/ToggleButtonTitled";
import Axios from "axios";
import { fns } from "../helpers/common";

import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import { DEFAULT_HOTEL, SanitizeHotelID, URLAPI } from "../configuration";
import ReportTableMetricsSummary from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import SmallPercentage from "../components/SmallPercentage";
import DataLoader from "../components/DataLoader";
import PickupEvaluateDialog from "../components/PickupEvaluateDialog";
import { HotelSelector } from "../components/TopBar";
import ConfirmModal from "../components/ConfirmModal";
import { use } from "react";
import { MANUAL_TARGETS_URL, TARGET_OFF } from "../helpers/targets";
import {
    COMPARISON_SAME_TIME_LAST_YEAR,
    COMPARISON_FILTER,
    COMPARISON_PREVIOUS_YEAR,
    DATATYPE_CHECKIN,
    GROUP_DATE_YEAR,
    GROUP_DATE_MONTH,
    GROUP_DATE_DAY,
    process_,
} from "../helpers/filters";
import { MIN_DATE, MAX_DATE } from "../helpers/dates";
import { process, round } from "../helpers/reports";

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const ROOM_NIGHTS = "ROOM_NIGHTS";
const REVENUE = "REVENUE";
const BOOKINGS = "BOOKINGS";
export const HORIZONS = [7, 14, 30, 60];

const ENABLE_HORIZON_BLEND = true;

const TARGET_COLOR = "#fac657";
const OTB_COLOR = "#5470c7";
const PREDICTED_COLOR = "#90cc74";
const GREEN_COLOR = "#16a34a";
const RED_COLOR = "#dc2626";

const AUTO_MODEL = "auto";
const DAYS_PICKUP = 90;

const TARGET_MODE_PERCENTAGE = "percentage";
const TARGET_MODE_MANUAL = "manual";
const TARGET_MODE_COMBINED = "combined";

export const fetchPickupPrediction = async ({
    urlApi,
    authToken,
    hotelID,
    asOfStr,
    days,
    modelName,
    autoModelName,
}) => {
    const params = {
        as_of: asOfStr,
        days,
        hotel_id: hotelID,
        // Do not include 'model' when AUTO, backend picks default model per horizon
    };

    if (modelName !== autoModelName) {
        // keep variable name
        params.model = modelName;
    }

    const resp = await Axios.get(`${urlApi}/AI/predict/pickup`, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        params,
    });

    const payload = resp?.data?.data ?? resp?.data ?? {};
    return {
        byHorizon: payload?.by_horizon ?? {},
        availableHorizons: Array.isArray(payload?.available_horizons)
            ? payload.available_horizons
            : null,
    };
};

export const safeVariancePct = (predicted, base) => {
    const p = Number(predicted ?? 0);
    const b = Number(base ?? 0);

    if (!Number.isFinite(p) || !Number.isFinite(b)) return "—";
    if (b === 0) return p === 0 ? 0 : "—";

    return ((p - b) / b) * 100;
};

export const getPredictedPickupByDay = ({
    predictCache,
    hotelID,
    modelKeyForCache,
    asOfStr,
    horizon,
    dayKeys,
    enableHorizonBlend,
}) => {
    if (!predictCache) return {};
    const hotelKey = SanitizeHotelID(hotelID);
    const asOfUtc = dayjs.utc(asOfStr);
    const hNum = Number(horizon);

    const getRowsForH = (h) => {
        const k = `${hotelKey}|${modelKeyForCache}|${asOfStr}|${Number(h)}`;
        const cached = predictCache[k]?.rows;
        return Array.isArray(cached) ? cached : [];
    };

    const rowsToPickupPerDay = (rows, h) => {
        const hatKey = `Pickup_${Number(h)}d_hat`;
        const acc = {};

        for (const row of rows) {
            const checkIn = row.CheckIn || row.checkin || row.check_in;
            if (!checkIn) continue;
            const k = dayjs.utc(checkIn).format("YYYY-MM-DD");

            const hat = Number(row[hatKey]);
            if (!Number.isFinite(hat)) continue;

            // Avoid negatives (keeps behavior consistent with PickupScreen)
            acc[k] = Math.max(0, hat);
        }

        return acc;
    };

    const need14 = hNum >= 14;
    const need30 = hNum >= 30;
    const need60 = hNum >= 60;

    const p7 = rowsToPickupPerDay(getRowsForH(7), 7);
    const p14 = need14 ? rowsToPickupPerDay(getRowsForH(14), 14) : {};
    const p30 = need30 ? rowsToPickupPerDay(getRowsForH(30), 30) : {};
    const p60 = need60 ? rowsToPickupPerDay(getRowsForH(60), 60) : {};

    const chooseH = (idx) => {
        if (idx < 0) return null;
        if (!enableHorizonBlend) return hNum;
        const tier = idx < 7 ? 7 : idx < 14 ? 14 : idx < 30 ? 30 : 60;
        return Math.min(tier, hNum);
    };

    const out = {};
    for (const k of dayKeys || []) {
        const idx = dayjs.utc(k).diff(asOfUtc, "day");
        const useH = chooseH(idx);
        if (useH == null) continue;

        out[k] =
            useH === 7
                ? p7[k]
                : useH === 14
                  ? p14[k]
                  : useH === 30
                    ? p30[k]
                    : p60[k];
        out[k] = Math.round(out[k]);
    }
    return out;
};

export const usePickupPredictor = ({
    urlApi,
    authToken,
    hotelID,
    horizon,
    modelName,
    asOfStr,
    days,
    fallbackHorizons,
    isReady,
    autoModelName,
}) => {
    const [isPredicting, setIsPredicting] = useState(false);
    const [needsPredict, setNeedsPredict] = useState(true);
    const [predictRows, setPredictRows] = useState([]);
    const [predictError, setPredictError] = useState(null);
    const [predictCache, setPredictCache] = useState({});

    const modelKeyForCache = useMemo(() => {
        return modelName === autoModelName ? "AUTO" : modelName;
    }, [modelName, autoModelName]);

    const cacheKey = useMemo(() => {
        const hotelKey = SanitizeHotelID(hotelID);
        return `${hotelKey}|${modelKeyForCache}|${asOfStr}|${Number(horizon)}`;
    }, [hotelID, modelKeyForCache, asOfStr, horizon]);

    const hasCachedForCurrent = Boolean(predictCache[cacheKey]);
    const hasPredictionForCurrent = Boolean(
        predictCache[cacheKey]?.rows?.length,
    );

    const lastAutoKeyRef = useRef(null);

    const handlePredict = useCallback(async () => {
        if (!authToken) return;

        setIsPredicting(true);
        try {
            setPredictError(null);

            const hotelKey = SanitizeHotelID(hotelID);

            const { byHorizon, availableHorizons } =
                await fetchPickupPrediction({
                    urlApi,
                    authToken,
                    hotelID: hotelKey,
                    asOfStr,
                    days,
                    modelName,
                    autoModelName,
                });

            const available = Array.isArray(availableHorizons)
                ? availableHorizons
                : fallbackHorizons;

            setPredictCache((prev) => {
                const next = { ...prev };
                available.forEach((h) => {
                    const rowsForHRaw = byHorizon?.[String(h)];
                    const rowsForH = Array.isArray(rowsForHRaw)
                        ? rowsForHRaw
                        : [];
                    const k = `${hotelKey}|${modelKeyForCache}|${asOfStr}|${Number(
                        h,
                    )}`;
                    next[k] = {
                        rows: rowsForH,
                        fetchedAt: dayjs.utc().toISOString(),
                    };
                });
                return next;
            });

            const currentRows = Array.isArray(byHorizon?.[String(horizon)])
                ? byHorizon[String(horizon)]
                : [];

            setPredictRows(currentRows);
            setNeedsPredict(false);
        } catch (e) {
            console.error("Predict request failed:", e);

            setPredictRows([]);
            setNeedsPredict(true);

            const apiMsg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "Unknown error";
            const status = e?.response?.status
                ? ` (HTTP ${e.response.status})`
                : "";

            setPredictError(
                `Could not load prediction data${status}: ${apiMsg}`,
            );
        } finally {
            setIsPredicting(false);
        }
    }, [
        authToken,
        urlApi,
        hotelID,
        asOfStr,
        days,
        modelName,
        autoModelName,
        fallbackHorizons,
        horizon,
        modelKeyForCache,
    ]);

    // When key changes, hydrate from cache if possible
    useEffect(() => {
        if (hasCachedForCurrent) {
            const cachedRows = predictCache[cacheKey]?.rows || [];
            setPredictRows(Array.isArray(cachedRows) ? cachedRows : []);
            setNeedsPredict(false);
        } else {
            setPredictRows([]);
            setNeedsPredict(true);
        }
    }, [cacheKey, hasCachedForCurrent, predictCache]);

    // Auto-run (same behavior as current PickupScreen)
    useEffect(() => {
        if (!isReady) return;
        if (!needsPredict) return;
        if (isPredicting) return;

        if (lastAutoKeyRef.current === cacheKey) return;
        lastAutoKeyRef.current = cacheKey;

        if (hasCachedForCurrent) return;

        handlePredict();
    }, [
        isReady,
        needsPredict,
        isPredicting,
        cacheKey,
        hasCachedForCurrent,
        handlePredict,
    ]);

    return {
        isPredicting,
        needsPredict,
        predictRows,
        predictError,
        setPredictError,
        predictCache,
        modelKeyForCache,
        cacheKey,
        hasCachedForCurrent,
        hasPredictionForCurrent,
        handlePredict,
    };
};

const sumRoomNightsFromTypes = (typesMap, keys) => {
    let total = 0;
    keys.forEach((k) => {
        total += Number(typesMap?.[k]?.RoomNights || 0);
    });
    return total;
};

export const buildTypesByCheckInDay = (rows, filterFn = null) => {
    const aux = {};
    (rows || []).forEach((d) => {
        const checkInRaw = d?.CheckIn;
        if (!checkInRaw) return;

        const key = dayjs.utc(checkInRaw).format("YYYY-MM-DD");
        const prev = aux[key] ?? { Revenue: 0, RoomNights: 0, Bookings: 0 };

        if (filterFn && !filterFn(d)) return;

        aux[key] = {
            Revenue: prev.Revenue + Number(d?.TotalPrice || 0),
            RoomNights: prev.RoomNights + Number(d?.RoomNights || 0),
            Bookings: prev.Bookings + 1,
        };
    });
    return aux;
};

export const filterByCheckInRange = ({ rows, fromKey, toKey }) => {
    const from = dayjs.utc(fromKey);
    const to = dayjs.utc(toKey);
    return (rows || []).filter((d) => {
        const checkin = dayjs.utc(d?.CheckIn);
        return (
            checkin.isSameOrAfter(from, "day") &&
            checkin.isSameOrBefore(to, "day")
        );
    });
};

export const filterSTLYLastYear = ({ rows, fromKey, toKey, asOfStr }) => {
    const fromLY = dayjs.utc(fromKey).subtract(1, "year");
    const toLY = dayjs.utc(toKey).subtract(1, "year");
    const asOfLYUtc = dayjs.utc(asOfStr).subtract(1, "year").endOf("day");

    return (rows || []).filter((d) => {
        const checkin = dayjs.utc(d?.CheckIn);
        const resDate = d?.ResDate ? dayjs.utc(d.ResDate) : null;

        return (
            checkin.isSameOrAfter(fromLY, "day") &&
            checkin.isSameOrBefore(toLY, "day") &&
            resDate &&
            resDate.isSameOrBefore(asOfLYUtc, "day")
        );
    });
};

export const computePeriodSummaryRoomNights = ({
    dayKeys,
    dataOTBTypes,
    predictedPickupByDay,
    dataOTBTypesLastYear,
    dataSTLYTypesLastYear,
    targetPct,
    targetRNOverride,
    stlyDayKeys,
}) => {
    const safeDayKeys = Array.isArray(dayKeys) ? dayKeys : [];

    // Current period totals
    const totalRN = sumRoomNightsFromTypes(dataOTBTypes, safeDayKeys);

    let pickupRN = 0;
    safeDayKeys.forEach((k) => {
        pickupRN += Number(predictedPickupByDay?.[k] || 0);
    });

    const predictedRN = totalRN + pickupRN;

    // Last year keys aligned with current dayKeys
    const dayKeysLY = safeDayKeys.map((k) =>
        dayjs.utc(k).subtract(1, "year").format("YYYY-MM-DD"),
    );

    const lyRN = sumRoomNightsFromTypes(dataOTBTypesLastYear, dayKeysLY);
    const lyStlyRN = sumRoomNightsFromTypes(
        dataSTLYTypesLastYear,
        stlyDayKeys ?? dayKeysLY,
    );

    const factor = 1 + Number(targetPct || 0) / 100;
    const targetRN = Number.isFinite(Number(targetRNOverride))
        ? Math.round(Number(targetRNOverride))
        : Math.round(lyStlyRN * factor);

    const varVsStlyPct =
        Number(lyStlyRN) > 0
            ? ((predictedRN - lyStlyRN) / lyStlyRN) * 100
            : NaN;

    const varVsTargetPct =
        Number(targetRN) > 0
            ? ((predictedRN - targetRN) / targetRN) * 100
            : NaN;

    return {
        totalRN: Math.round(totalRN),
        pickupRN: Math.round(pickupRN),
        predictedRN: Math.round(predictedRN),
        lyRN: Math.round(lyRN),
        lyStlyRN: Math.round(lyStlyRN),
        targetRN,
        varVsStlyPct,
        varVsTargetPct,
    };
};

export const sumFieldFromTypes = (typesMap, keys, field) => {
    const safeKeys = Array.isArray(keys) ? keys : [];
    const f = String(field || "");
    let total = 0;
    safeKeys.forEach((k) => {
        total += Number(typesMap?.[k]?.[f] || 0);
    });
    return total;
};

export const computePeriodSummary = ({
    dayKeys,
    dataOTBTypes,
    predictedPickupByDay,
    dataOTBTypesLastYear,
    dataSTLYTypesLastYear,
    targetPct,
    targetOverride,
    field = "RoomNights",
}) => {
    const safeDayKeys = Array.isArray(dayKeys) ? dayKeys : [];

    const total = sumFieldFromTypes(dataOTBTypes, safeDayKeys, field);

    let pickup = 0;
    safeDayKeys.forEach((k) => {
        pickup += Number(predictedPickupByDay?.[k] || 0);
    });

    const predicted = total + pickup;

    const dayKeysLY = safeDayKeys.map((k) =>
        dayjs.utc(k).subtract(1, "year").format("YYYY-MM-DD"),
    );

    const ly = sumFieldFromTypes(dataOTBTypesLastYear, dayKeysLY, field);
    const lyStly = sumFieldFromTypes(dataSTLYTypesLastYear, dayKeysLY, field);

    const factor = 1 + Number(targetPct || 0) / 100;
    const target = Number.isFinite(Number(targetOverride))
        ? Math.round(Number(targetOverride))
        : Math.round(lyStly * factor);

    const varVsStlyPct =
        Number(lyStly) > 0 ? ((predicted - lyStly) / lyStly) * 100 : NaN;

    const varVsTargetPct =
        Number(target) > 0 ? ((predicted - target) / target) * 100 : NaN;

    return {
        total: Math.round(total),
        pickup: Math.round(pickup),
        predicted: Math.round(predicted),
        ly: Math.round(ly),
        lyStly: Math.round(lyStly),
        target,
        varVsStlyPct,
        varVsTargetPct,
    };
};

export const buildAdrIndexByCheckIn = (rows, filterFn = null) => {
    const map = {};
    const sums = {};
    const counts = {};
    (rows || []).forEach((d) => {
        try {
            if (filterFn && !filterFn(d)) return;
            if (!filterFn && String(d?.Type) !== "Special Offer") return;

            const rate = Number(d?.DailyRate);
            if (!Number.isFinite(rate)) return;

            const checkInRaw = d?.CheckIn;
            if (!checkInRaw) return;

            const key = dayjs.utc(checkInRaw).format("YYYY-MM-DD");
            if (!key) return;

            sums[key] = (sums[key] || 0) + rate;
            counts[key] = (counts[key] || 0) + 1;
        } catch (_) {}
    });

    Object.keys(sums).forEach((k) => {
        const c = counts[k] || 0;
        if (c > 0) map[k] = sums[k] / c;
    });

    return map;
};

export const getADRForDateFromIndex = ({
    isoDate,
    manualAdrValue,
    adrIndexByCheckIn,
    adrSortedKeys,
}) => {
    if (Number.isFinite(manualAdrValue)) return manualAdrValue;

    const key = dayjs.utc(isoDate).format("YYYY-MM-DD");
    if (Number.isFinite(adrIndexByCheckIn?.[key]))
        return adrIndexByCheckIn[key];

    const lyKey = dayjs.utc(isoDate).subtract(1, "year").format("YYYY-MM-DD");
    if (Number.isFinite(adrIndexByCheckIn?.[lyKey]))
        return adrIndexByCheckIn[lyKey];

    const safeSorted = Array.isArray(adrSortedKeys) ? adrSortedKeys : [];
    if (safeSorted.length === 0) return 0;

    const target = dayjs.utc(key);
    let best = safeSorted[0];
    let bestDiff = Math.abs(dayjs.utc(best).diff(target, "day"));
    for (let i = 1; i < safeSorted.length; i++) {
        const k = safeSorted[i];
        const diff = Math.abs(dayjs.utc(k).diff(target, "day"));
        if (diff < bestDiff) {
            best = k;
            bestDiff = diff;
        }
    }
    return Number(adrIndexByCheckIn?.[best] || 0);
};

export const buildRoomsPerBookingIndexByCheckIn = (rows) => {
    const map = {};
    const rnSums = {};
    const bookingCounts = {};
    (rows || []).forEach((d) => {
        try {
            const rn = Number(d?.RoomNights);
            if (!Number.isFinite(rn)) return;

            const checkInRaw = d?.CheckIn;
            if (!checkInRaw) return;

            const key = dayjs.utc(checkInRaw).format("YYYY-MM-DD");
            if (!key) return;

            rnSums[key] = (rnSums[key] || 0) + rn;
            bookingCounts[key] = (bookingCounts[key] || 0) + 1;
        } catch (_) {}
    });

    Object.keys(rnSums).forEach((k) => {
        const count = bookingCounts[k] || 0;
        if (count > 0) map[k] = rnSums[k] / count;
    });

    return map;
};

export const getRoomsPerBookingForDateFromIndex = ({
    isoDate,
    roomsPerBookingIndexByCheckIn,
    roomsPerBookingSortedKeys,
}) => {
    const key = dayjs.utc(isoDate).format("YYYY-MM-DD");

    let v = Number(roomsPerBookingIndexByCheckIn?.[key]);
    if (Number.isFinite(v) && v > 0) return v;

    const lyKey = dayjs.utc(isoDate).subtract(1, "year").format("YYYY-MM-DD");
    v = Number(roomsPerBookingIndexByCheckIn?.[lyKey]);
    if (Number.isFinite(v) && v > 0) return v;

    const safeSorted = Array.isArray(roomsPerBookingSortedKeys)
        ? roomsPerBookingSortedKeys
        : [];
    if (safeSorted.length === 0) return 1;

    const target = dayjs.utc(key);
    let best = safeSorted[0];
    let bestDiff = Math.abs(dayjs.utc(best).diff(target, "day"));
    for (let i = 1; i < safeSorted.length; i++) {
        const k = safeSorted[i];
        const diff = Math.abs(dayjs.utc(k).diff(target, "day"));
        if (diff < bestDiff) {
            best = k;
            bestDiff = diff;
        }
    }

    const fallbackVal = Number(roomsPerBookingIndexByCheckIn?.[best] || 0);
    if (Number.isFinite(fallbackVal) && fallbackVal > 0) return fallbackVal;

    return 1;
};

export const convertRoomNightsPerDayMapToRevenue = ({
    dayKeys,
    perDayMap,
    getADRForDate,
}) => {
    const safeDayKeys = Array.isArray(dayKeys) ? dayKeys : [];
    const out = {};
    for (const k of safeDayKeys) {
        const rn = Number(perDayMap?.[k] || 0);
        const adr = getADRForDate(k);
        out[k] = Math.round(rn * adr);
    }
    return out;
};

export const convertRoomNightsPerDayMapToBookings = ({
    dayKeys,
    perDayMap,
    getRoomsPerBookingForDate,
}) => {
    const safeDayKeys = Array.isArray(dayKeys) ? dayKeys : [];
    const out = {};
    for (const k of safeDayKeys) {
        const rn = Number(perDayMap?.[k] || 0);
        const roomsPerBooking = getRoomsPerBookingForDate(k);
        if (!Number.isFinite(roomsPerBooking) || roomsPerBooking <= 0) {
            out[k] = 0;
            continue;
        }
        out[k] = Math.ceil(Math.max(0, rn / roomsPerBooking));
    }
    return out;
};

export const buildManualInterpolatedSeries = ({
    dayKeys,
    manualTargetsByDay,
}) => {
    const safeDayKeys = Array.isArray(dayKeys) ? dayKeys : [];
    const keys = Object.keys(manualTargetsByDay || {}).sort();
    if (keys.length === 0) return safeDayKeys.map(() => null);

    const anchors = keys
        .map((k) => ({
            k,
            idx: safeDayKeys.indexOf(k),
            v: Number(manualTargetsByDay[k]),
        }))
        .filter((a) => a.idx >= 0 && Number.isFinite(a.v))
        .sort((a, b) => a.idx - b.idx);

    if (anchors.length === 0) return safeDayKeys.map(() => null);

    const out = new Array(safeDayKeys.length).fill(null);

    if (anchors.length === 1) return out.map(() => Math.round(anchors[0].v));

    for (let i = 0; i < anchors[0].idx; i++) out[i] = Math.round(anchors[0].v);
    for (let i = anchors[anchors.length - 1].idx; i < out.length; i++)
        out[i] = Math.round(anchors[anchors.length - 1].v);

    for (let a = 0; a < anchors.length - 1; a++) {
        const left = anchors[a];
        const right = anchors[a + 1];
        const span = right.idx - left.idx;

        out[left.idx] = Math.round(left.v);
        out[right.idx] = Math.round(right.v);

        for (let i = left.idx + 1; i < right.idx; i++) {
            const t = (i - left.idx) / span;
            out[i] = Math.round(left.v + (right.v - left.v) * t);
        }
    }

    return out;
};

const PickupScreen = () => {
    const dataBookings = useSelector((state) => state.dataBookings);
    const dataSimple = useSelector((state) => state.dataSimple);
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const { auth } = useSelector((state) => state);

    const [horizon, setHorizon] = useState(30);
    const [metric, setMetric] = useState(ROOM_NIGHTS);
    const metricLabel =
        metric === REVENUE
            ? "Revenue"
            : metric === BOOKINGS
              ? "Bookings"
              : "Room Nights";

    const [isTargetConfirmOpen, setIsTargetConfirmOpen] = useState(false);
    const [pendingTargetAction, setPendingTargetAction] = useState(null);
    const [draftTargetMarker, setDraftTargetMarker] = useState(null);
    const draftTargetMarkerRef = useRef(null);

    // from = today (locked), to ≤ today + DAYS_PICKUP
    const todayUtc = dayjs.utc();
    const minArrivalUtc = todayUtc.startOf("month");
    const maxArrivalUtc = dayjs.utc().add(DAYS_PICKUP, "day");
    const [fromDate, setFromDate] = useState(minArrivalUtc);
    const [toDate, setToDate] = useState(
        todayUtc.endOf("month").startOf("day"),
    );
    const dayKeys = useMemo(() => {
        const start = dayjs.utc(fromDate).startOf("day");
        const end = dayjs.utc(toDate).startOf("day");
        const arr = [];
        for (let d = start; !d.isAfter(end, "day"); d = d.add(1, "day")) {
            arr.push(d.format("YYYY-MM-DD"));
        }
        return arr;
    }, [fromDate, toDate]);

    const [manualAdr, setManualAdr] = useState("");

    const manualAdrValue = useMemo(() => {
        const raw = String(manualAdr ?? "").trim();
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [manualAdr]);

    const isManualAdrInvalid = useMemo(() => {
        const raw = String(manualAdr ?? "").trim();
        if (!raw) return false; // empty is allowed => fallback to calculated
        const n = Number(raw);
        return !(Number.isFinite(n) && n > 0);
    }, [manualAdr]);

    const [targetPct, setTargetPct] = useState(5);
    const [comparison, setComparison] = useState(COMPARISON_PREVIOUS_YEAR);

    const asOfStr = todayUtc.format("YYYY-MM-DD");

    const [modelName, setModelName] = useState(AUTO_MODEL);

    const [trainedHotels, setTrainedHotels] = useState([]);

    const [modelsMap, setModelsMap] = useState({});
    const [modelsError, setModelsError] = useState(null);
    const [isLoadingModels, setIsLoadingModels] = useState(false);

    const [arrivalPreset, setArrivalPreset] = useState("initial");
    const arrivalPresetOptions = useMemo(() => {
        const base = dayjs.utc();

        const buildMonth = (offset, value = null) => {
            const m = base.add(offset, "month");
            return {
                value: value ? value : `M${offset}`,
                label: m.format("MMMM"),
                getRange: () => {
                    const monthStart = m.startOf("month").startOf("day");
                    const monthEnd = m.endOf("month").startOf("day");

                    return { from: monthStart, to: monthEnd };
                },
            };
        };

        return [buildMonth(0, "initial"), buildMonth(1), buildMonth(2)];
    }, []);

    useEffect(() => {
        if (!auth?.user?.jwt) return;

        let cancelled = false;

        const run = async () => {
            try {
                const resp = await Axios.get(
                    `${URLAPI}/AI/predict/pickup/list_hotels`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                        },
                    },
                );

                const payload = resp?.data?.data ?? resp?.data ?? {};
                const hotels = Array.isArray(payload?.hotels)
                    ? payload.hotels
                    : Array.isArray(payload)
                      ? payload
                      : [];

                if (!cancelled) setTrainedHotels(hotels);
            } catch (e) {
                console.error("Failed to load trained hotels list:", e);
                // Keep current behavior (no restriction if we can't load the list)
                if (!cancelled) setTrainedHotels([]);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [URLAPI, auth?.user?.jwt]); // eslint-disable-line react-hooks/exhaustive-deps

    const modelKeysForHorizon = useMemo(() => {
        const h = Number(horizon);
        const keys = Object.keys(modelsMap || {});

        if (ENABLE_HORIZON_BLEND) {
            const needed = HORIZONS.filter((x) => x <= h);
            const strong = keys.filter((k) => {
                const arr = modelsMap[k];
                return (
                    Array.isArray(arr) && needed.every((n) => arr.includes(n))
                );
            });

            const baseList = (strong.length > 0 ? strong : keys)
                .filter((k) => {
                    const arr = modelsMap[k];
                    return Array.isArray(arr) && arr.includes(h);
                })
                .sort()
                .map((k) =>
                    String(k).replace(".joblib", "").replace(".pkl", ""),
                );

            /* Prepend AUTO always */
            return [AUTO_MODEL, ...baseList];
        }

        const baseList = keys
            .filter((k) => {
                const arr = modelsMap[k];
                return Array.isArray(arr) && arr.includes(h);
            })
            .sort()
            .map((k) => String(k).replace(".joblib", "").replace(".pkl", ""));

        /*  Prepend AUTO always */
        return [AUTO_MODEL, ...baseList];
    }, [modelsMap, horizon, ENABLE_HORIZON_BLEND]);

    const isReadyForPredict =
        Boolean(auth?.user?.jwt) &&
        !isLoadingModels &&
        modelKeysForHorizon.length > 0;

    const {
        isPredicting,
        needsPredict,
        // predictRows,
        predictError,
        setPredictError,
        predictCache,
        modelKeyForCache,
        hasCachedForCurrent,
        hasPredictionForCurrent,
        handlePredict,
    } = usePickupPredictor({
        urlApi: URLAPI,
        authToken: auth?.user?.jwt ?? "",

        hotelID,
        horizon,
        modelName,
        asOfStr,

        days: DAYS_PICKUP,
        fallbackHorizons: HORIZONS,
        isReady: isReadyForPredict,
        autoModelName: AUTO_MODEL,
    });
    const [ciPct, setCiPct] = useState(80);
    const ciKey = useMemo(() => `p${Number(ciPct)}`, [ciPct]);
    const ciQrssByDay = useMemo(() => {
        if (!predictCache) return {};
        const hotelKey = SanitizeHotelID(hotelID);
        const asOfUtc = dayjs.utc(asOfStr);
        const hNum = Number(horizon);

        const getRowsForH = (h) => {
            const k = `${hotelKey}|${modelKeyForCache}|${asOfStr}|${Number(h)}`;
            const cached = predictCache[k]?.rows;
            return Array.isArray(cached) ? cached : [];
        };

        // Convert rows → per-day sqrt(sum(q^2)) for a given horizon
        const rowsToQrssPerDay = (rows, h) => {
            const hatKey = `Pickup_${Number(h)}d_hat`;
            const hiKey = `Pickup_${Number(h)}d_hi_${ciKey}`;
            const loKey = `Pickup_${Number(h)}d_lo_${ciKey}`;

            const acc = {};
            for (const row of rows) {
                const checkIn = row.CheckIn || row.checkin || row.check_in;
                if (!checkIn) continue;
                const k = dayjs.utc(checkIn).format("YYYY-MM-DD");

                const hat = Number(row[hatKey]);
                const hi = Number(row[hiKey]);
                const lo = Number(row[loKey]);

                let q = null;
                if (Number.isFinite(hi) && Number.isFinite(hat))
                    q = Math.max(0, hi - hat);
                else if (Number.isFinite(lo) && Number.isFinite(hat))
                    q = Math.max(0, hat - lo);

                if (q != null) acc[k] = (acc[k] || 0) + q * q;
            }
            Object.keys(acc).forEach((k) => (acc[k] = Math.sqrt(acc[k])));
            return acc;
        };

        // Precompute CI maps for needed horizons
        const need14 = hNum >= 14;
        const need30 = hNum >= 30;
        const need60 = hNum >= 60;

        const q7 = rowsToQrssPerDay(getRowsForH(7), 7);
        const q14 = need14 ? rowsToQrssPerDay(getRowsForH(14), 14) : {};
        const q30 = need30 ? rowsToQrssPerDay(getRowsForH(30), 30) : {};
        const q60 = need60 ? rowsToQrssPerDay(getRowsForH(60), 60) : {};

        // Day → which horizon to use (tiered) and clamp to selected horizon
        const chooseH = (idx) => {
            if (idx < 0) return null;
            if (!ENABLE_HORIZON_BLEND) return hNum;
            const tier = idx < 7 ? 7 : idx < 14 ? 14 : idx < 30 ? 30 : 60;
            return Math.min(tier, hNum);
        };

        const out = {};
        for (const k of dayKeys) {
            const idx = dayjs.utc(k).diff(asOfUtc, "day");
            const useH = chooseH(idx);
            if (useH == null) continue;

            // Pick the qrss map for the selected horizon per day
            out[k] =
                useH === 7
                    ? q7[k]
                    : useH === 14
                      ? q14[k]
                      : useH === 30
                        ? q30[k]
                        : q60[k];
        }

        return out;
    }, [
        predictCache,
        horizon,
        ciKey,
        hotelID,
        modelKeyForCache,
        asOfStr,
        dayKeys,
        ENABLE_HORIZON_BLEND,
    ]);

    const currentHotelKey = useMemo(() => {
        const raw = SanitizeHotelID(hotelID);
        return String(raw ?? "").trim();
    }, [hotelID]);

    const currentHotelSiteIDs = useMemo(() => {
        const hotels = dataSimple?.data?.Hotels || [];
        const h = hotels.find(
            (it) => String(it?.HotelID ?? "") === String(currentHotelKey),
        );

        const raw = h?.SiteIDs ?? "";
        if (raw == null) return [];

        return String(raw)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    }, [dataSimple, currentHotelKey]);

    const trainedSiteIDsSet = useMemo(() => {
        const set = new Set();

        (trainedHotels || []).forEach((h) => {
            // API may return {site, name} or plain values. Be tolerant.
            if (h && typeof h === "object") {
                if (h.site != null) {
                    set.add(String(h.site).trim());
                } else if (Array.isArray(h.sites)) {
                    h.sites.forEach(
                        (s) => s != null && set.add(String(s).trim()),
                    );
                } else if (h.SiteIDs != null) {
                    String(h.SiteIDs)
                        .split(",")
                        .forEach((s) => s && set.add(String(s).trim()));
                }
            } else if (h != null) {
                set.add(String(h).trim());
            }
        });

        return set;
    }, [trainedHotels]);

    const allowedHotelIds = useMemo(() => {
        // While list_hotels hasn't loaded, do NOT restrict (avoid empty selector)
        if (!trainedHotels || trainedHotels.length === 0) return null;

        const hotels = dataSimple?.data?.Hotels || [];

        return hotels
            .filter((h) => {
                const raw = h?.SiteIDs ?? "";
                const siteIds = String(raw)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);

                return siteIds.some((sid) =>
                    trainedSiteIDsSet.has(String(sid)),
                );
            })
            .map((h) => h.HotelID)
            .filter((id) => id != null);
    }, [trainedHotels, dataSimple, trainedSiteIDsSet]);

    const isCurrentHotelTrained = useMemo(() => {
        if (!trainedHotels || trainedHotels.length === 0) return true;
        if (!currentHotelSiteIDs || currentHotelSiteIDs.length === 0)
            return true;
        return currentHotelSiteIDs.some((sid) =>
            trainedSiteIDsSet.has(String(sid)),
        );
    }, [trainedHotels, currentHotelSiteIDs, trainedSiteIDsSet]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                setIsLoadingModels(true);
                setModelsError(null);
                const resp = await Axios.get(
                    `${URLAPI}/AI/predict/pickup/list_models`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                        },
                    },
                );
                const payload = resp?.data?.data ?? resp?.data ?? {};
                const available = payload?.available_models ?? {};
                if (!cancelled) setModelsMap(available || {});
            } catch (e) {
                if (!cancelled)
                    setModelsError(e?.message || "Failed to load models list");
            } finally {
                if (!cancelled) setIsLoadingModels(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [URLAPI, auth?.user?.jwt]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (modelKeysForHorizon.length === 0) return;
        if (!modelKeysForHorizon.includes(modelName)) {
            const preferred = ["hgb_log", "huber"].find((k) =>
                modelKeysForHorizon.includes(k),
            );
            /* If preferred is missing, fall back to AUTO first */
            setModelName(preferred || AUTO_MODEL || modelKeysForHorizon[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [horizon, JSON.stringify(modelKeysForHorizon)]);

    // Target
    const [targetMode, setTargetMode] = useState(TARGET_MODE_PERCENTAGE);
    const [isTargetEditMode, setIsTargetEditMode] = useState(false);

    const [manualTargetsByDay, setManualTargetsByDay] = useState({});
    const [isLoadingManualTargets, setIsLoadingManualTargets] = useState(false);
    const [manualTargetsError, setManualTargetsError] = useState(null);

    const chartRef = useRef(null);

    const targetType = "pickup";
    const targetSubType = useMemo(() => {
        if (metric === ROOM_NIGHTS) return "rooms";
        if (metric === REVENUE) return "revenue";
        return "bookings";
    }, [metric]);

    const hotelKeyForTargets = useMemo(() => {
        const raw = SanitizeHotelID(hotelID);
        return Number(raw);
    }, [hotelID]);

    const { user } = useSelector((state) => state.auth);
    const userIdForTargets = user.user_id;

    const fromKey = useMemo(
        () => dayjs.utc(fromDate).format("YYYY-MM-DD"),
        [fromDate],
    );

    const toKey = useMemo(
        () => dayjs.utc(toDate).format("YYYY-MM-DD"),
        [toDate],
    );

    const allBookingRows = useMemo(() => {
        if (
            !dataBookings ||
            (dataBookings.status !== "loaded" &&
                dataBookings.status !== "partiallyLoaded")
        ) {
            return [];
        }
        return Array.isArray(dataBookings.data) ? dataBookings.data : [];
    }, [dataBookings]);

    const isSpecialOffer = React.useCallback(
        (d) => String(d?.Type) === "Special Offer",
        [],
    );

    // Current period (OTB)
    const dataOTB = useMemo(() => {
        return filterByCheckInRange({
            rows: allBookingRows,
            fromKey,
            toKey,
        });
    }, [allBookingRows, fromKey, toKey]);

    const { dataOTBTypes, dataSTLYTypesLastYear } = useMemo(() => {
        const dataOTBTypes = {};
        const dataSTLYTypesLastYear = {};

        if (
            !allBookingRows.length ||
            String(dataBookings?.hotelID) !== currentHotelKey
        ) {
            return { dataOTBTypes, dataSTLYTypesLastYear };
        }

        const { entries } = process_({
            js: {
                dataType: DATATYPE_CHECKIN,
                groupBy: [GROUP_DATE_YEAR, GROUP_DATE_MONTH, GROUP_DATE_DAY],
                fromDay: fromKey,
                toDay: toKey,
                fromArrivalDay: MIN_DATE,
                toArrivalDay: MAX_DATE,
                fromResDateDay: MIN_DATE,
                toResDateDay:
                    comparison === COMPARISON_SAME_TIME_LAST_YEAR
                        ? asOfStr
                        : MAX_DATE,
                filters: {
                    Comparison: comparison,
                    Cancellations: "included",
                    Target: TARGET_OFF,
                },
            },
            dataBookings,
            hotelID,
            processFn: (rows, id) =>
                process(rows, id, { roundRoomAndExtras: false }),
        });

        for (const { keys, result, resultOneYearBefore } of Object.values(
            entries,
        )) {
            const date = keys
                .map(([, value]) => String(value).padStart(2, "0"))
                .join("-");
            // A range starting on Feb 29 can receive last year's Feb 28.
            const key = date < fromKey ? fromKey : date;

            for (const [days, values] of [
                [dataOTBTypes, result],
                [dataSTLYTypesLastYear, resultOneYearBefore],
            ]) {
                if (values?.bookingsRooms == null) continue;
                days[key] = {
                    Revenue: values.revenueRoomAndExtras,
                    Bookings: values.bookingsRooms,
                    RoomNights: values.roomNightsTotal,
                };
            }
        }

        return { dataOTBTypes, dataSTLYTypesLastYear };
    }, [
        allBookingRows,
        dataBookings,
        currentHotelKey,
        hotelID,
        fromKey,
        toKey,
        asOfStr,
        comparison,
    ]);

    // Last year (same arrival window, shifted by 1y)
    const fromKeyLY = useMemo(
        () => dayjs.utc(fromKey).subtract(1, "year").format("YYYY-MM-DD"),
        [fromKey],
    );

    const toKeyLY = useMemo(
        () => dayjs.utc(toKey).subtract(1, "year").format("YYYY-MM-DD"),
        [toKey],
    );

    const dataOTBLastYear = useMemo(() => {
        return filterByCheckInRange({
            rows: allBookingRows,
            fromKey: fromKeyLY,
            toKey: toKeyLY,
        });
    }, [allBookingRows, fromKeyLY, toKeyLY]);

    const lastYearFilter = metric === REVENUE ? isSpecialOffer : null;

    const dataOTBTypesLastYear = useMemo(
        () => buildTypesByCheckInDay(dataOTBLastYear, lastYearFilter),
        [dataOTBLastYear, lastYearFilter],
    );

    const predictedPickupByDay = useMemo(() => {
        return getPredictedPickupByDay({
            predictCache,
            hotelID,
            modelKeyForCache,
            asOfStr,
            horizon,
            dayKeys,
            enableHorizonBlend: ENABLE_HORIZON_BLEND,
        });
    }, [predictCache, hotelID, modelKeyForCache, asOfStr, horizon, dayKeys]);

    const fetchManualTargets = React.useCallback(async () => {
        if (!hotelKeyForTargets) return;

        setIsLoadingManualTargets(true);
        setManualTargetsError(null);

        try {
            const resp = await Axios.get(MANUAL_TARGETS_URL, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                },
                params: {
                    hotel_id: hotelKeyForTargets,
                    target_type: targetType,
                    date_granularity: "DAY",
                    target_subtype: targetSubType,
                    from_date: dayjs.utc(fromDate).format("YYYY-MM-DD"),
                    to_date: dayjs.utc(toDate).format("YYYY-MM-DD"),
                },
            });

            const rows = resp?.data?.data ?? resp?.data ?? [];
            const map = {};
            (Array.isArray(rows) ? rows : []).forEach((r) => {
                const k = dayjs.utc(r.TargetDate).format("YYYY-MM-DD");
                const v = Number(r.TargetValue);
                if (k && Number.isFinite(v)) map[k] = v;
            });

            setManualTargetsByDay(map);
        } catch (e) {
            setManualTargetsByDay({});
            setManualTargetsError(
                e?.message || "Failed to load manual targets",
            );
        } finally {
            setIsLoadingManualTargets(false);
        }
    }, [
        auth?.user?.jwt,
        hotelKeyForTargets,
        targetType,
        targetSubType,
        fromDate,
        toDate,
    ]);

    const upsertManualTargetPoint = React.useCallback(
        async (dateKey, value) => {
            if (!userIdForTargets) return;

            await Axios.post(
                MANUAL_TARGETS_URL,
                {
                    hotel_id: hotelKeyForTargets,
                    date_granularity: "DAY",
                    target_date: dateKey,
                    target_type: targetType,
                    target_subtype: targetSubType,
                    target_value: value,
                    user_id: userIdForTargets,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                    },
                },
            );

            setManualTargetsByDay((prev) => ({ ...prev, [dateKey]: value }));
        },
        [
            auth?.user?.jwt,
            hotelKeyForTargets,
            targetType,
            targetSubType,
            userIdForTargets,
        ],
    );

    const deleteManualTargetPoint = React.useCallback(
        async (dateKey) => {
            await Axios.delete(MANUAL_TARGETS_URL, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth?.user?.jwt ?? ""}`,
                },
                params: {
                    hotel_id: hotelKeyForTargets,
                    date_granularity: "DAY",
                    target_date: dateKey,
                    target_type: targetType,
                    target_subtype: targetSubType,
                },
            });

            setManualTargetsByDay((prev) => {
                const next = { ...prev };
                delete next[dateKey];
                return next;
            });
        },
        [auth?.user?.jwt, hotelKeyForTargets, targetType, targetSubType],
    );

    // Load anchor points when needed
    useEffect(() => {
        if (targetMode === TARGET_MODE_PERCENTAGE) {
            // Optional: you can still load them in background if you want
            return;
        }
        fetchManualTargets();
    }, [targetMode, fetchManualTargets]);

    const percentTargetSeries = useMemo(() => {
        const field =
            metric === ROOM_NIGHTS
                ? "RoomNights"
                : metric === REVENUE
                  ? "Revenue"
                  : "Bookings";

        const factor = 1 + (Number(targetPct) || 0) / 100;
        let totalLY = 0;

        return dayKeys.map((k) => {
            const previousTarget = Math.round(totalLY * factor);
            totalLY += Number(dataSTLYTypesLastYear[k]?.[field] || 0);

            // Round the running total so daily targets add up to the period target.
            return Math.round(totalLY * factor) - previousTarget;
        });
    }, [dayKeys, metric, targetPct, dataSTLYTypesLastYear]);

    const manualInterpolatedSeries = useMemo(() => {
        return buildManualInterpolatedSeries({ dayKeys, manualTargetsByDay });
    }, [dayKeys, manualTargetsByDay]);

    const combinedTargetSeries = useMemo(() => {
        return dayKeys.map((k, i) => {
            const anchor = manualTargetsByDay?.[k];
            if (anchor != null && Number.isFinite(Number(anchor)))
                return Math.round(Number(anchor));
            return percentTargetSeries[i];
        });
    }, [dayKeys, manualTargetsByDay, percentTargetSeries]);

    const targetSeries = useMemo(() => {
        const base =
            targetMode === TARGET_MODE_MANUAL
                ? manualInterpolatedSeries
                : targetMode === TARGET_MODE_COMBINED
                  ? combinedTargetSeries
                  : percentTargetSeries;

        // If target is undefined, treat it as 0
        return dayKeys.map((_, i) => {
            const v = Array.isArray(base) ? base[i] : undefined;
            return v === null ? 0 : v;
        });
    }, [
        targetMode,
        manualInterpolatedSeries,
        combinedTargetSeries,
        percentTargetSeries,
        dayKeys,
    ]);

    const targetOverride = useMemo(
        () =>
            targetMode === TARGET_MODE_PERCENTAGE
                ? undefined
                : targetSeries.reduce((sum, value) => sum + (value || 0), 0),
        [targetMode, targetSeries],
    );

    // END Target

    const arrivalPeriodADR = useMemo(() => {
        const rows = dataOTB.filter(isSpecialOffer);
        const roomNights = rows.reduce(
            (sum, d) => sum + (Number(d.RoomNights) || 0),
            0,
        );
        const roomRevenue = rows.reduce(
            (sum, d) => sum + (Number(d.RoomRevenue) || 0),
            0,
        );
        const revenue =
            roomRevenue ||
            rows.reduce((sum, d) => sum + (Number(d.TotalPrice) || 0), 0);

        console.log("Pickup ADR:", {
            bookings: rows.length,
            roomNights,
            roomRevenue,
            revenue,
        });

        return roomNights > 0 ? Number((revenue / roomNights).toFixed(2)) : 0;
    }, [dataOTB, isSpecialOffer]);

    const getADRForDate = useCallback(
        () => manualAdrValue ?? arrivalPeriodADR,
        [manualAdrValue, arrivalPeriodADR],
    );

    const roomsPerBookingIndexByCheckIn = useMemo(() => {
        return buildRoomsPerBookingIndexByCheckIn(dataBookings?.data);
    }, [dataBookings]);

    const roomsPerBookingSortedKeys = useMemo(
        () => Object.keys(roomsPerBookingIndexByCheckIn).sort(),
        [roomsPerBookingIndexByCheckIn],
    );

    const getRoomsPerBookingForDate = useCallback(
        (isoDate) =>
            getRoomsPerBookingForDateFromIndex({
                isoDate,
                roomsPerBookingIndexByCheckIn,
                roomsPerBookingSortedKeys,
            }),
        [roomsPerBookingIndexByCheckIn, roomsPerBookingSortedKeys],
    );

    const predictedPickupRevenueByDay = useMemo(() => {
        return convertRoomNightsPerDayMapToRevenue({
            dayKeys,
            perDayMap: predictedPickupByDay,
            getADRForDate,
        });
    }, [dayKeys, predictedPickupByDay, getADRForDate]);

    const predictedPickupBookingsByDay = useMemo(() => {
        return convertRoomNightsPerDayMapToBookings({
            dayKeys,
            perDayMap: predictedPickupByDay,
            getRoomsPerBookingForDate,
        });
    }, [dayKeys, predictedPickupByDay, getRoomsPerBookingForDate]);

    const ciQrssRevenueByDay = useMemo(() => {
        const out = {};
        dayKeys.forEach((k) => {
            const adr = getADRForDate(k);
            const q = Number(ciQrssByDay[k]);
            if (Number.isFinite(q)) out[k] = Math.abs(q * adr);
        });
        return out;
    }, [dayKeys, ciQrssByDay, getADRForDate]);

    const summaryTotalsRevenue = useMemo(() => {
        const factor = 1 + (Number(targetPct) || 0) / 100;

        let totalRev = 0;
        dayKeys.forEach((k) => {
            totalRev += Number(dataOTBTypes[k]?.Revenue || 0);
        });

        let pickupRev = 0;
        dayKeys.forEach((k) => {
            pickupRev += Number(predictedPickupRevenueByDay[k] || 0);
        });

        const predictedRev = totalRev + pickupRev;

        let lyRev = 0;
        dayKeys.forEach((k) => {
            const keyLY = dayjs.utc(k).subtract(1, "year").format("YYYY-MM-DD");
            lyRev += Number(dataOTBTypesLastYear[keyLY]?.Revenue || 0);
        });

        const lyStlyRev = sumFieldFromTypes(
            dataSTLYTypesLastYear,
            dayKeys,
            "Revenue",
        );

        const targetRev = targetOverride ?? Math.round(lyStlyRev * factor);

        // CI in revenue: scale RN band by ADR
        let ciLoTotalRev = 0;
        let ciHiTotalRev = 0;
        dayKeys.forEach((k) => {
            const baseRev = Number(dataOTBTypes[k]?.Revenue || 0);
            const hatSumRN = Number(predictedPickupByDay[k] || 0);
            const qRssRN = Number(ciQrssByDay[k]);

            const adr = getADRForDate(k);
            if (Number.isFinite(qRssRN)) {
                const lo = Math.max(
                    0,
                    Math.round(baseRev + (hatSumRN - qRssRN) * adr),
                );
                const hi = Math.max(
                    0,
                    Math.round(baseRev + (hatSumRN + qRssRN) * adr),
                );
                ciLoTotalRev += lo;
                ciHiTotalRev += hi;
            } else {
                const mid = Math.max(0, Math.round(baseRev + hatSumRN * adr));
                ciLoTotalRev += mid;
                ciHiTotalRev += mid;
            }
        });

        return {
            totalRev: round(totalRev, 0),
            predictedRev: round(predictedRev, 0),
            lyRev: Math.round(lyRev),
            lyStlyRev: round(lyStlyRev, 0),
            targetRev,
            pickupRev: Math.round(pickupRev),
            ciLoTotalRev: Math.round(ciLoTotalRev),
            ciHiTotalRev: Math.round(ciHiTotalRev),
        };
    }, [
        dayKeys,
        dataOTBTypes,
        dataOTBTypesLastYear,
        dataSTLYTypesLastYear,
        predictedPickupByDay,
        ciQrssByDay,
        predictedPickupRevenueByDay,
        getADRForDate,
        targetPct,
        targetOverride,
    ]);

    const summaryTotalsBookings = useMemo(() => {
        let totalBookings = 0;
        dayKeys.forEach((k) => {
            totalBookings += Number(dataOTBTypes[k]?.Bookings || 0);
        });

        let pickupBookings = 0;
        dayKeys.forEach((k) => {
            pickupBookings += Number(predictedPickupBookingsByDay[k] || 0);
        });

        const predictedBookings = totalBookings + pickupBookings;

        let lyBookings = 0;
        dayKeys.forEach((k) => {
            const keyLY = dayjs.utc(k).subtract(1, "year").format("YYYY-MM-DD");
            lyBookings += Number(dataOTBTypesLastYear[keyLY]?.Bookings || 0);
        });

        const lyStlyBookings = sumFieldFromTypes(
            dataSTLYTypesLastYear,
            dayKeys,
            "Bookings",
        );

        const factor = 1 + (Number(targetPct) || 0) / 100;
        const targetBookings =
            targetOverride ?? Math.round(lyStlyBookings * factor);

        // Simple CI for bookings: we use the midpoint (no band for now)
        let ciLoTotalBookings = 0;
        let ciHiTotalBookings = 0;
        dayKeys.forEach((k) => {
            const base = Number(dataOTBTypes[k]?.Bookings || 0);
            const pickup = Number(predictedPickupBookingsByDay[k] || 0);
            const total = base + pickup;
            ciLoTotalBookings += total;
            ciHiTotalBookings += total;
        });

        return {
            totalBookings: Math.round(totalBookings),
            predictedBookings: Math.round(predictedBookings),
            lyBookings: Math.round(lyBookings),
            lyStlyBookings: Math.round(lyStlyBookings),
            targetBookings,
            pickupBookings: Math.round(pickupBookings),
            ciLoTotalBookings: Math.round(ciLoTotalBookings),
            ciHiTotalBookings: Math.round(ciHiTotalBookings),
        };
    }, [
        dayKeys,
        dataOTBTypes,
        dataOTBTypesLastYear,
        dataSTLYTypesLastYear,
        predictedPickupBookingsByDay,
        targetPct,
        targetOverride,
    ]);
    useEffect(() => {
        const inst = chartRef.current?.getEchartsInstance?.();
        if (!inst) return;

        const zr = inst.getZr();

        const computeSnap = (e) => {
            const pixel = [e?.offsetX, e?.offsetY];

            if (!inst.containPixel({ gridIndex: 0 }, pixel)) return null;

            const dataXY = inst.convertFromPixel(
                { xAxisIndex: 0, yAxisIndex: 0 },
                pixel,
            );
            if (!Array.isArray(dataXY) || dataXY.length < 2) return null;

            const xIdx = Math.round(Number(dataXY[0]));
            const yVal = Number(dataXY[1]);

            if (!Number.isFinite(xIdx)) return null;
            if (xIdx < 0 || xIdx >= dayKeys.length) return null;

            const dateKey = dayKeys[xIdx];
            if (!dateKey) return null;

            const isAlt = Boolean(e?.event?.altKey);
            const hasAnchor = manualTargetsByDay?.[dateKey] != null;

            // Only delete when Alt is pressed AND there's an anchor on that day
            const wantsDelete = isAlt && hasAnchor;

            const xLabel = dayjs.utc(dateKey).format("DD MMM");

            // If deleting, snap to the anchor value; otherwise use mouse y
            const rawValue = wantsDelete
                ? Number(manualTargetsByDay[dateKey])
                : yVal;
            const value = Number.isFinite(rawValue)
                ? Math.max(0, Math.round(rawValue))
                : 0;

            const snapped = inst.convertToPixel(
                { xAxisIndex: 0, yAxisIndex: 0 },
                [xLabel, value],
            );
            if (!Array.isArray(snapped) || snapped.length < 2) return null;

            const intent = wantsDelete ? "delete" : "upsert";
            const label = wantsDelete
                ? `Delete ${xLabel}: ${value}`
                : `${xLabel}: ${value}`;

            return {
                dateKey,
                value,
                x: snapped[0],
                y: snapped[1],
                label,
                intent,
                canDelete: wantsDelete,
            };
        };

        const clearDraft = () => {
            draftTargetMarkerRef.current = null;
            setDraftTargetMarker(null);
        };

        const onMove = (e) => {
            if (!isTargetEditMode) return clearDraft();
            if (targetMode === TARGET_MODE_PERCENTAGE) return clearDraft();
            if (isTargetConfirmOpen) return; // freeze while modal is open

            const snap = computeSnap(e);
            if (!snap) return clearDraft();

            const last = draftTargetMarkerRef.current;
            if (
                last &&
                last.dateKey === snap.dateKey &&
                last.value === snap.value
            ) {
                return;
            }

            draftTargetMarkerRef.current = {
                dateKey: snap.dateKey,
                value: snap.value,
            };
            setDraftTargetMarker(snap);
        };

        const onClick = (e) => {
            if (!isTargetEditMode) return;
            if (targetMode === TARGET_MODE_PERCENTAGE) return;
            if (isTargetConfirmOpen) return;

            const snap = computeSnap(e);
            if (!snap) return;

            if (snap.intent === "delete") {
                setPendingTargetAction({
                    type: "delete",
                    dateKey: snap.dateKey,
                    value: Number(manualTargetsByDay[snap.dateKey]),
                });
                setIsTargetConfirmOpen(true);
                return;
            }

            setPendingTargetAction({
                type: "upsert",
                dateKey: snap.dateKey,
                value: snap.value,
            });
            setIsTargetConfirmOpen(true);
        };

        const onContextMenu = (e) => {
            if (!isTargetEditMode) return;
            if (targetMode === TARGET_MODE_PERCENTAGE) return;

            // Right click => delete anchor (discoverable)
            e?.event?.preventDefault?.();

            const snap = computeSnap(e);
            if (!snap) return;

            if (manualTargetsByDay?.[snap.dateKey] == null) return;

            setPendingTargetAction({
                type: "delete",
                dateKey: snap.dateKey,
                value: Number(manualTargetsByDay[snap.dateKey]),
            });
            setIsTargetConfirmOpen(true);
        };

        zr.on("mousemove", onMove);
        zr.on("mouseout", clearDraft);
        zr.on("click", onClick);
        zr.on("contextmenu", onContextMenu);

        return () => {
            try {
                zr.off("mousemove", onMove);
                zr.off("mouseout", clearDraft);
                zr.off("click", onClick);
                zr.off("contextmenu", onContextMenu);
            } catch (error) {
                // Ignore cleanup errors from disposed zrender instance
            }
        };
    }, [
        chartRef,
        dayKeys,
        isTargetEditMode,
        targetMode,
        isTargetConfirmOpen,
        manualTargetsByDay,
    ]);
    const chartOption = useMemo(() => {
        const formatChartValue = (value) =>
            Number.isFinite(value)
                ? fns(
                      hotelID,
                      value,
                      metric === REVENUE
                          ? { left: "currency", maxDecimals: 2 }
                          : { maxDecimals: 0 },
                  )
                : "—";

        const field =
            metric === ROOM_NIGHTS
                ? "RoomNights"
                : metric === REVENUE
                  ? "Revenue"
                  : "Bookings";

        const baseSeries = dayKeys.map((k) => dataOTBTypes[k]?.[field] ?? 0);

        const xLabels = dayKeys.map((k) => dayjs.utc(k).format("DD MMM"));
        const weekendMarkArea = {
            silent: true,
            itemStyle: { color: "rgba(0,0,0,0.04)" },
            data: dayKeys.flatMap((k, i) =>
                [5, 6, 0].includes(dayjs.utc(k).day())
                    ? [[{ xAxis: i - 0.5 }, { xAxis: i + 0.5 }]]
                    : [],
            ),
        };

        const pickupSeries =
            metric === ROOM_NIGHTS
                ? dayKeys.map((k) => predictedPickupByDay[k] ?? 0)
                : metric === REVENUE
                  ? dayKeys.map((k) => predictedPickupRevenueByDay[k] ?? 0)
                  : metric === BOOKINGS
                    ? dayKeys.map((k) => predictedPickupBookingsByDay[k] ?? 0)
                    : [];

        // const factor = 1 + (Number(targetPct) || 0) / 100;
        // const targetSeries = dayKeys.map((k) => {
        //     const lastYearKey = dayjs
        //         .utc(k)
        //         .subtract(1, "year")
        //         .format("YYYY-MM-DD");
        //     const baseLY = dataOTBTypesLastYear[lastYearKey]?.[field];
        //     if (baseLY == null) return null;
        //     return Math.round(Number(baseLY) * factor);
        // });

        const anchorSet = new Set(Object.keys(manualTargetsByDay || {}));

        const targetWithDots = dayKeys.map((k, i) => {
            const t = targetSeries[i];
            if (t == null) return null;

            const otb = Number(baseSeries[i] || 0);
            const pred = Number(pickupSeries[i] || 0);
            const sum = otb + pred;

            const dotColor = sum >= Number(t) ? GREEN_COLOR : RED_COLOR;
            const isAnchor = anchorSet.has(k);

            const out = {
                value: Number(t),
                itemStyle: { color: dotColor, borderColor: dotColor },
                // NOTE: do NOT set symbolSize for normal points -> they keep series.symbolSize (4)
            };

            // Manual/combined: anchors bigger, intermediates normal size
            if (targetMode !== TARGET_MODE_PERCENTAGE && isAnchor) {
                out.symbolSize = 9;
            }

            return out;
        });

        const series = [];
        const predictedLabel = `Predicted ${metricLabel}`;

        if (
            metric === ROOM_NIGHTS ||
            metric === REVENUE ||
            metric === BOOKINGS
        ) {
            // Shared stacked bars for RN, Revenue and Bookings
            const stackName =
                metric === BOOKINGS ? "pickup_stack_bookings" : "pickup_stack";

            series.push({
                name:
                    metric === ROOM_NIGHTS
                        ? "OTB Confirmed room nights"
                        : metric === REVENUE
                          ? "OTB Revenue"
                          : "OTB Confirmed bookings",
                type: "bar",
                data: baseSeries,
                stack: stackName,
                lineStyle: { color: OTB_COLOR },
            });
            series.push({
                name: predictedLabel,
                type: "bar",
                data: pickupSeries,
                stack: stackName,
                lineStyle: { color: PREDICTED_COLOR },
            });
        }

        let tooltip = {};

        if (metric === ROOM_NIGHTS || metric === REVENUE) {
            // Optional: CI band also for revenue (scale qRss by ADR)
            const ciLoSeries = dayKeys.map((k, i) => {
                const base = Number(baseSeries[i] || 0);
                const hatSum = Number(pickupSeries[i] || 0);
                if (metric === ROOM_NIGHTS) {
                    const qRss = ciQrssByDay[k];
                    if (!Number.isFinite(qRss)) return null;
                    return Math.max(0, Math.round(base + (hatSum - qRss)));
                } else {
                    const qRssRev = ciQrssRevenueByDay[k];
                    if (!Number.isFinite(qRssRev)) return null;
                    return Math.max(0, Math.round(base + (hatSum - qRssRev)));
                }
            });

            const ciHiSeries = dayKeys.map((k, i) => {
                const base = Number(baseSeries[i] || 0);
                const hatSum = Number(pickupSeries[i] || 0);
                if (metric === ROOM_NIGHTS) {
                    const qRss = ciQrssByDay[k];
                    if (!Number.isFinite(qRss)) return null;
                    return Math.max(0, Math.round(base + (hatSum + qRss)));
                } else {
                    const qRssRev = ciQrssRevenueByDay[k];
                    if (!Number.isFinite(qRssRev)) return null;
                    return Math.max(0, Math.round(base + (hatSum + qRssRev)));
                }
            });

            const ciRangeSeries = dayKeys.map((_, i) => {
                const lo = ciLoSeries[i],
                    hi = ciHiSeries[i];
                return lo != null && hi != null ? hi - lo : null;
            });
            const hasBand = ciRangeSeries.some((v) => v != null);
            const ciLegendLabel = `Confidence Interval ${ciPct}%`;
            const ciLegendColor = "rgba(128,128,128,0.6)";
            const ciAreaColor = "rgba(128,128,128,0.25)";

            if (hasBand) {
                series.push({
                    name: ciLegendLabel,
                    type: "line",
                    data: ciLoSeries,
                    symbol: "none",
                    smooth: true,
                    lineStyle: { width: 0, opacity: 0, color: ciLegendColor },
                    areaStyle: { opacity: 0 },
                    itemStyle: { color: ciLegendColor },
                    stack: "ci_band",
                    tooltip: { show: false },
                    emphasis: { disabled: true },
                    z: 0,
                });
                series.push({
                    name: ciLegendLabel,
                    type: "line",
                    data: ciRangeSeries,
                    symbol: "none",
                    smooth: true,
                    lineStyle: { width: 0, opacity: 0, color: ciLegendColor },
                    areaStyle: { color: ciAreaColor },
                    itemStyle: { color: ciLegendColor },
                    stack: "ci_band",
                    emphasis: { disabled: true },
                    z: 0,
                });
            }

            tooltip = {
                trigger: "axis",
                formatter: (params) => {
                    const idx =
                        params && params.length ? params[0].dataIndex : 0;
                    const dateKey = dayKeys[idx];
                    const lines = [];

                    if (params[0] && params[0].axisValueLabel != null) {
                        lines.push(`${params[0].axisValueLabel}`);
                    }

                    const otbVal = Number(baseSeries[idx] || 0);
                    const predictedVal = Number(pickupSeries[idx] || 0);
                    const totalVal = otbVal + predictedVal;

                    const targetVal = Array.isArray(targetSeries)
                        ? targetSeries[idx]
                        : null;

                    let arrowForPredicted = "";
                    if (Number.isFinite(targetVal)) {
                        const sum = otbVal + predictedVal;
                        if (sum > Number(targetVal)) {
                            arrowForPredicted = `<span style="color:${GREEN_COLOR}">↑</span>`;
                        } else if (sum < Number(targetVal)) {
                            arrowForPredicted = `<span style="color:${RED_COLOR}">↓</span>`;
                        } else {
                            arrowForPredicted = `<span style="color:${TARGET_COLOR}">=</span>`;
                        }
                    }

                    for (const p of params) {
                        if (p.seriesName !== ciLegendLabel) {
                            if (p.seriesName === predictedLabel) {
                                lines.push(
                                    `${p.marker}${predictedLabel}: <b>${formatChartValue(p.value)}</b>${
                                        arrowForPredicted
                                            ? ` <b>${arrowForPredicted}</b>`
                                            : ""
                                    }`,
                                );
                            } else {
                                lines.push(
                                    `${p.marker}${p.seriesName}: <b>${formatChartValue(p.value)}</b>`,
                                );
                            }
                        }
                    }

                    if (Number.isFinite(totalVal)) {
                        lines.push(
                            `<b>Total:</b> ${formatChartValue(totalVal)}`,
                        );
                    }

                    if (metric === REVENUE) {
                        const adr = getADRForDate(dateKey);
                        if (Number.isFinite(adr)) {
                            lines.push(
                                `<b>ADR used:</b> ${formatChartValue(Math.round(adr))}${Math.round(adr)}`,
                            );
                        }
                    }

                    if (hasBand) {
                        const lo = ciLoSeries[idx];
                        const hi = ciHiSeries[idx];
                        if (lo != null && hi != null) {
                            const ciP = params.find(
                                (p) => p.seriesName === ciLegendLabel,
                            );
                            const mk = ciP ? ciP.marker : "";
                            lines.push(
                                `${mk}${ciLegendLabel}: <b>${formatChartValue(lo)} - ${formatChartValue(hi)}</b>`,
                            );
                        }
                    }

                    return lines.join("<br/>");
                },
            };
        } else if (metric === BOOKINGS) {
            // Tooltip for Bookings (no CI band for now)
            tooltip = {
                trigger: "axis",
                formatter: (params) => {
                    const idx =
                        params && params.length ? params[0].dataIndex : 0;
                    const lines = [];

                    if (params[0] && params[0].axisValueLabel != null) {
                        lines.push(`${params[0].axisValueLabel}`);
                    }

                    const otbVal = Number(baseSeries[idx] || 0);
                    const predictedVal = Number(pickupSeries[idx] || 0);
                    const totalVal = otbVal + predictedVal;

                    const targetVal = Array.isArray(targetSeries)
                        ? targetSeries[idx]
                        : null;

                    let arrowForPredicted = "";
                    if (Number.isFinite(targetVal)) {
                        const sum = totalVal;
                        if (sum > Number(targetVal)) {
                            arrowForPredicted = `<span style="color:${GREEN_COLOR}">↑</span>`;
                        } else if (sum < Number(targetVal)) {
                            arrowForPredicted = `<span style="color:${RED_COLOR}">↓</span>`;
                        } else {
                            arrowForPredicted = `<span style="color:${TARGET_COLOR}">=</span>`;
                        }
                    }

                    for (const p of params) {
                        lines.push(
                            `${p.marker}${p.seriesName}: <b>${formatChartValue(p.value)}${p.value}</b>${
                                p.seriesName === predictedLabel &&
                                arrowForPredicted
                                    ? ` <b>${arrowForPredicted}</b>`
                                    : ""
                            }`,
                        );
                    }

                    lines.push(`<b>Total:</b> ${formatChartValue(totalVal)}`);

                    return lines.join("<br/>");
                },
            };
        }

        const asOfKey = dayjs.utc(asOfStr).format("YYYY-MM-DD");

        // Booking window date = today + (horizon - 1) days (matching the previous intention)
        const bookingWindowKey = dayjs
            .utc(asOfStr)
            .add(Math.max(Number(horizon) - 1, 0), "day")
            .format("YYYY-MM-DD");

        const todayIdx = dayKeys.indexOf(asOfKey);
        const bwIdx = dayKeys.indexOf(bookingWindowKey);

        const markLines = [
            ...(todayIdx >= 0
                ? [
                      {
                          xAxis: xLabels[todayIdx],
                          name: "Today",
                          lineStyle: { type: "solid" },
                      },
                  ]
                : []),
            ...(bwIdx >= 0
                ? [
                      {
                          xAxis: xLabels[bwIdx],
                          name: "Booking Window",
                          lineStyle: { type: "dashed" },
                      },
                  ]
                : []),
        ];
        series.push({
            name: `Target ${metricLabel}`,
            type: "line",
            data: targetWithDots,
            markArea: weekendMarkArea,
            smooth: true,
            symbol: "circle",
            showSymbol: true,
            showAllSymbol: true,
            symbolSize: 4,
            lineStyle: { width: 4, color: TARGET_COLOR },
            markLine: {
                silent: true,
                symbol: "none",
                lineStyle: { type: "dashed" },
                label: { formatter: () => "" },
                data: markLines,
            },
        });

        if (isTargetEditMode && targetMode !== TARGET_MODE_PERCENTAGE) {
            tooltip = { show: false };
        }

        const draftGraphic =
            isTargetEditMode &&
            targetMode !== TARGET_MODE_PERCENTAGE &&
            draftTargetMarker
                ? [
                      {
                          id: "targetDraftPoint",
                          type: "group",
                          silent: true,
                          children:
                              draftTargetMarker.intent === "delete"
                                  ? [
                                        {
                                            type: "line",
                                            shape: {
                                                x1: draftTargetMarker.x - 7,
                                                y1: draftTargetMarker.y - 7,
                                                x2: draftTargetMarker.x + 7,
                                                y2: draftTargetMarker.y + 7,
                                            },
                                            style: {
                                                stroke: RED_COLOR,
                                                lineWidth: 2,
                                                opacity:
                                                    draftTargetMarker.canDelete
                                                        ? 1
                                                        : 0.35,
                                            },
                                        },
                                        {
                                            type: "line",
                                            shape: {
                                                x1: draftTargetMarker.x - 7,
                                                y1: draftTargetMarker.y + 7,
                                                x2: draftTargetMarker.x + 7,
                                                y2: draftTargetMarker.y - 7,
                                            },
                                            style: {
                                                stroke: RED_COLOR,
                                                lineWidth: 2,
                                                opacity:
                                                    draftTargetMarker.canDelete
                                                        ? 1
                                                        : 0.35,
                                            },
                                        },
                                        {
                                            type: "text",
                                            style: {
                                                text: draftTargetMarker.label,
                                                x: draftTargetMarker.x + 10,
                                                y: draftTargetMarker.y - 10,
                                                fill: "#111",
                                                backgroundColor:
                                                    "rgba(255,255,255,0.9)",
                                                padding: [2, 4],
                                                font: "12px sans-serif",
                                            },
                                        },
                                    ]
                                  : [
                                        {
                                            type: "circle",
                                            shape: {
                                                cx: draftTargetMarker.x,
                                                cy: draftTargetMarker.y,
                                                r: 6,
                                            },
                                            style: {
                                                fill: TARGET_COLOR,
                                                opacity: 0.9,
                                            },
                                        },
                                        {
                                            type: "text",
                                            style: {
                                                text: draftTargetMarker.label,
                                                x: draftTargetMarker.x + 10,
                                                y: draftTargetMarker.y - 10,
                                                fill: "#111",
                                                backgroundColor:
                                                    "rgba(255,255,255,0.9)",
                                                padding: [2, 4],
                                                font: "12px sans-serif",
                                            },
                                        },
                                    ],
                      },
                  ]
                : [];

        return {
            grid: {
                left: 20,
                right: 20,
                top: 20,
                bottom: 40,
                containLabel: true,
            },
            tooltip: { trigger: "axis" },
            legend: { top: 0 },
            tooltip,
            graphic: draftGraphic,
            xAxis: {
                type: "category",
                data: xLabels,
                axisTick: { alignWithLabel: true },
            },
            yAxis: {
                type: "value",
                axisLabel: { formatter: formatChartValue },
            },
            series,
        };
    }, [
        dayKeys,
        dataOTBTypes,
        dataOTBTypesLastYear,
        metric,
        predictedPickupByDay,
        predictedPickupRevenueByDay,
        predictedPickupBookingsByDay,
        horizon,
        targetPct,
        ciPct,
        ciQrssByDay,
        ciQrssRevenueByDay,
        getADRForDate,
        targetMode,
        manualTargetsByDay,
        targetSeries,
        draftTargetMarker,
        isTargetEditMode,
    ]);

    const summaryTotals = useMemo(() => {
        const base = computePeriodSummaryRoomNights({
            dayKeys,
            dataOTBTypes,
            predictedPickupByDay,
            dataOTBTypesLastYear,
            dataSTLYTypesLastYear,
            targetPct,
            targetRNOverride: targetOverride,
            stlyDayKeys: dayKeys,
        });

        // Keep CI calculation here (helper does not include CI today)
        let ciLoTotal = 0;
        let ciHiTotal = 0;

        dayKeys.forEach((k) => {
            const baseRN = Number(dataOTBTypes[k]?.RoomNights || 0);
            const hatSum = Number(predictedPickupByDay[k] || 0);
            const qRss = Number(ciQrssByDay[k]);

            if (Number.isFinite(qRss)) {
                const lo = Math.max(0, Math.round(baseRN + (hatSum - qRss)));
                const hi = Math.max(0, Math.round(baseRN + (hatSum + qRss)));
                ciLoTotal += lo;
                ciHiTotal += hi;
            } else {
                const mid = Math.max(0, Math.round(baseRN + hatSum));
                ciLoTotal += mid;
                ciHiTotal += mid;
            }
        });

        return {
            ...base,
            ciLoTotal: Math.round(ciLoTotal),
            ciHiTotal: Math.round(ciHiTotal),
        };
    }, [
        dayKeys,
        dataOTBTypes,
        predictedPickupByDay,
        dataOTBTypesLastYear,
        dataSTLYTypesLastYear,
        targetPct,
        targetSeries,
        targetMode,
        ciQrssByDay,
    ]);

    const otbLabel = `OTB ${metricLabel}`;
    const predictedLabel = `OTB + Predicted ${metricLabel}`;
    const pickupLabel = `Predicted Pickup ${metricLabel}`;
    const comparisonLabel =
        comparison === COMPARISON_SAME_TIME_LAST_YEAR
            ? "STLY"
            : COMPARISON_FILTER[comparison].label;

    const stlyLabel = `${comparisonLabel} ${metricLabel}`;
    const targetLabel = `Target ${metricLabel}`;
    const varianceStlyLabel = `Variance vs ${comparisonLabel} (%)`;
    const varianceTargetLabel = "Variance vs Target (%)";
    const forecastCILabel = "Prediction Range (CI)";

    const summaryData = useMemo(() => {
        const hasPrediction = !needsPredict && hasPredictionForCurrent;
        const noPredictionPlaceholder = "—";

        if (metric === REVENUE) {
            const {
                totalRev,
                predictedRev,
                lyRev,
                lyStlyRev,
                targetRev,
                pickupRev,
                ciLoTotalRev,
                ciHiTotalRev,
            } = summaryTotalsRevenue;

            const predictedVal = predictedRev;
            const stlyVal = lyStlyRev;
            const targetVal = targetRev;

            // const varVsStlyPct =
            //     hasPrediction && Number(stlyVal) > 0
            //         ? ((Number(predictedVal) - Number(stlyVal)) /
            //               Number(stlyVal)) *
            //           100
            //         : NaN;

            // const varVsTargetPct =
            //     hasPrediction && Number(targetVal) > 0
            //         ? ((Number(predictedVal) - Number(targetVal)) /
            //               Number(targetVal)) *
            //           100
            //         : NaN;

            const varVsStlyPct = hasPrediction
                ? safeVariancePct(predictedVal, stlyVal)
                : "—";
            const varVsTargetPct = hasPrediction
                ? safeVariancePct(predictedVal, targetVal)
                : "—";

            return {
                group0: {
                    keys: [],
                    result: {
                        [otbLabel]: totalRev,
                        [predictedLabel]: hasPrediction
                            ? predictedRev
                            : noPredictionPlaceholder,
                        [pickupLabel]: hasPrediction
                            ? pickupRev
                            : noPredictionPlaceholder,
                        [stlyLabel]: lyStlyRev,
                        [targetLabel]: targetRev,
                        [varianceStlyLabel]: varVsStlyPct,
                        [varianceTargetLabel]: varVsTargetPct,
                        [forecastCILabel]: hasPrediction
                            ? `${fns(hotelID, ciLoTotalRev, { left: "currency", maxDecimals: 2 })} – ${fns(hotelID, ciHiTotalRev, { left: "currency", maxDecimals: 2 })}`
                            : noPredictionPlaceholder,
                    },
                    resultOneYearBefore: { [otbLabel]: lyStlyRev },
                },
            };
        }

        if (metric === BOOKINGS) {
            const {
                totalBookings,
                predictedBookings,
                lyBookings,
                lyStlyBookings,
                targetBookings,
                pickupBookings,
                ciLoTotalBookings,
                ciHiTotalBookings,
            } = summaryTotalsBookings;

            const predictedVal = predictedBookings;
            const stlyVal = lyStlyBookings;
            const targetVal = targetBookings;

            // const varVsStlyPct =
            //     hasPrediction && Number(stlyVal) > 0
            //         ? ((Number(predictedVal) - Number(stlyVal)) /
            //               Number(stlyVal)) *
            //           100
            //         : NaN;

            // const varVsTargetPct =
            //     hasPrediction && Number(targetVal) > 0
            //         ? ((Number(predictedVal) - Number(targetVal)) /
            //               Number(targetVal)) *
            //           100
            //         : NaN;

            const varVsStlyPct = hasPrediction
                ? safeVariancePct(predictedVal, stlyVal)
                : "—";
            const varVsTargetPct = hasPrediction
                ? safeVariancePct(predictedVal, targetVal)
                : "—";

            return {
                group0: {
                    keys: [],
                    result: {
                        [otbLabel]: totalBookings,
                        [predictedLabel]: hasPrediction
                            ? predictedBookings
                            : noPredictionPlaceholder,
                        [pickupLabel]: hasPrediction
                            ? pickupBookings
                            : noPredictionPlaceholder,
                        [stlyLabel]: lyStlyBookings,
                        [targetLabel]: targetBookings,
                        [varianceStlyLabel]: varVsStlyPct,
                        [varianceTargetLabel]: varVsTargetPct,
                        [forecastCILabel]: hasPrediction
                            ? `${ciLoTotalBookings} - ${ciHiTotalBookings}`
                            : noPredictionPlaceholder,
                    },
                    resultOneYearBefore: { [otbLabel]: lyStlyBookings },
                },
            };
        }

        // ROOM_NIGHTS
        const {
            totalRN,
            predictedRN,
            lyRN,
            lyStlyRN,
            targetRN,
            pickupRN,
            ciLoTotal,
            ciHiTotal,
        } = summaryTotals;

        const predictedVal = predictedRN;
        const stlyVal = lyStlyRN;
        const targetVal = targetRN;

        // const varVsStlyPct =
        //     hasPrediction && Number(stlyVal) > 0
        //         ? ((Number(predictedVal) - Number(stlyVal)) / Number(stlyVal)) *
        //           100
        //         : NaN;

        // const varVsTargetPct =
        //     hasPrediction && Number(targetVal) > 0
        //         ? ((Number(predictedVal) - Number(targetVal)) /
        //               Number(targetVal)) *
        //           100
        //         : NaN;

        const varVsStlyPct = hasPrediction
            ? safeVariancePct(predictedVal, stlyVal)
            : "—";
        const varVsTargetPct = hasPrediction
            ? safeVariancePct(predictedVal, targetVal)
            : "—";

        return {
            group0: {
                keys: [],
                result: {
                    [otbLabel]: totalRN,
                    [predictedLabel]: hasPrediction
                        ? predictedRN
                        : noPredictionPlaceholder,
                    [pickupLabel]: hasPrediction
                        ? pickupRN
                        : noPredictionPlaceholder,
                    [stlyLabel]: lyStlyRN,
                    [targetLabel]: targetRN,
                    [varianceStlyLabel]: varVsStlyPct,
                    [varianceTargetLabel]: varVsTargetPct,
                    [forecastCILabel]: hasPrediction
                        ? `${ciLoTotal} - ${ciHiTotal}`
                        : noPredictionPlaceholder,
                },
                resultOneYearBefore: { [otbLabel]: lyStlyRN },
            },
        };
    }, [
        metric,
        summaryTotals,
        summaryTotalsRevenue,
        summaryTotalsBookings,
        needsPredict,
    ]);

    const summaryJs = useMemo(() => {
        const fieldsOrder = [
            otbLabel,
            predictedLabel,
            pickupLabel,
            stlyLabel,
            targetLabel,
            varianceStlyLabel,
            varianceTargetLabel,
            forecastCILabel,
        ];

        return {
            fromDay: dayjs.utc(fromDate).toISOString(),
            toDay: dayjs.utc(toDate).toISOString(),
            dataType: DATATYPE_CHECKIN,
            filters: { Comparison: comparison },
            hideFields: [],
            fieldsOrder,
            hidePercentageFor: fieldsOrder,
        };
    }, [
        fromDate,
        toDate,
        otbLabel,
        predictedLabel,
        pickupLabel,
        stlyLabel,
        targetLabel,
        varianceStlyLabel,
        varianceTargetLabel,
        forecastCILabel,
    ]);

    const [isEvaluateOpen, setIsEvaluateOpen] = useState(false);

    const bookingWindowDateKey = useMemo(() => {
        return dayjs
            .utc(asOfStr)
            .add(Math.max(Number(horizon) - 1, 0), "day")
            .format("YYYY-MM-DD");
    }, [asOfStr, horizon]);

    const hasPredictionsForSelection = useMemo(() => {
        return dayKeys.some((k) => Number.isFinite(predictedPickupByDay?.[k]));
    }, [dayKeys, predictedPickupByDay]);

    const [dismissNoPredictionsAlert, setDismissNoPredictionsAlert] =
        useState(false);

    // Re-open the alert when the user changes the selection and it is still empty
    useEffect(() => {
        const shouldBeVisible =
            hasCachedForCurrent &&
            !needsPredict &&
            !isPredicting &&
            !predictError &&
            (!hasPredictionForCurrent || !hasPredictionsForSelection);

        if (shouldBeVisible) setDismissNoPredictionsAlert(false);
    }, [
        fromKey,
        toKey,
        horizon,
        modelName,
        hotelID,
        needsPredict,
        isPredicting,
        predictError,
        hasPredictionsForSelection,
    ]);

    return (
        <Container maxWidth={false} sx={{ mt: 2 }}>
            <DataLoader Reservations SimpleData />
            <Typography variant="h4">Pickup Prediction</Typography>

            <Typography variant="subtitle1" color="initial" sx={{ mt: 1 }}>
                ⚠️This is a work in progress report. This is NOT a final
                product. <br /> <br />
                Pickup prediction is based on room nights. <br />
                There are 4 horizon available: 7 days, 14 days, 30 days and 60
                days. <br />
                A horizon represents the predicted room nights if we were N days
                in the future. In other words, how many rooms will be sold in N
                days for the following days. <br />
                We predict a maximum of 60 days. The further the days, the
                bigger the estimated error. <br />
            </Typography>

            <br />

            <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                sx={{ justifyContent: "flex-start", alignItems: "flex-end" }}
            >
                <ToggleButtonTitled
                    TITLE={"Show by"}
                    values={{
                        [ROOM_NIGHTS]: "Rooms Nights",
                        [REVENUE]: "Revenue",
                        [BOOKINGS]: "Bookings",
                    }}
                    valueSet={metric}
                    onChange={(e, v) => {
                        // keep variable name
                        setMetric(v);
                    }}
                    TITLE_TOOLTIP={
                        "Switch between different forecast metrics - Room Nights, Revenue, or Bookings."
                    }
                />
                <Stack>
                    <Tooltip
                        title={
                            "Select how far into the future to forecast new bookings expected to be made."
                        }
                        placement={"top"}
                        arrow
                    >
                        <Typography>Booking Window</Typography>
                    </Tooltip>
                    <Select
                        value={horizon}
                        onChange={(e) => setHorizon(e.target.value)}
                        sx={{ maxHeight: 50 }}
                    >
                        {HORIZONS.map((h) => (
                            <MenuItem key={h} value={h}>
                                {h}d
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
                <DateRangePicker
                    title="Arrival Period"
                    tittle_tooltip="Choose the guest arrival date range you want to analyse."
                    isRangePicker={true}
                    // presets wiring
                    presets={arrivalPresetOptions}
                    presetValue={arrivalPreset}
                    onChangePresetValue={(v) => setArrivalPreset(v)}
                    customPreset={{
                        value: "CUSTOM",
                        label: "Custom",
                    }}
                    onChangeFromDate={(d) => {
                        setFromDate(d);
                    }}
                    onChangeToDate={(d) => {
                        setToDate(d);
                    }}
                    fromDateProps={{
                        value: fromDate,
                        minDate: minArrivalUtc,
                        maxDate: maxArrivalUtc,
                    }}
                    toDateProps={{
                        value: toDate,
                        minDate: minArrivalUtc,
                        maxDate: maxArrivalUtc,
                    }}
                />
                <Stack>
                    <Typography>Model</Typography>
                    {modelKeysForHorizon.length > 0 && (
                        <Select
                            value={modelName}
                            onChange={(e) => setModelName(e.target.value)}
                            sx={{ maxHeight: 50, minWidth: 140 }}
                            disabled={isLoadingModels}
                        >
                            {/* "auto" is already included by modelKeysForHorizon */}
                            {modelKeysForHorizon.map((k) => (
                                <MenuItem key={k} value={k}>
                                    {k === AUTO_MODEL ? "auto" : k}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </Stack>
                {metric === REVENUE && (
                    <Stack>
                        <Tooltip
                            title={
                                "Override ADR used to convert Room Nights → Revenue. Leave empty to use the calculated ADR."
                            }
                            placement="top"
                            arrow
                        >
                            <Typography>ADR</Typography>
                        </Tooltip>

                        <TextField
                            value={manualAdr}
                            onChange={(e) => setManualAdr(e.target.value)}
                            placeholder={
                                arrivalPeriodADR
                                    ? String(arrivalPeriodADR)
                                    : "—"
                            }
                            error={isManualAdrInvalid}
                            inputProps={{
                                inputMode: "decimal",
                                pattern: "\\d*(\\.\\d*)?",
                            }}
                            size="small"
                            sx={{
                                maxWidth: 140,
                                minWidth: 120,
                                "& .MuiInputBase-root": { height: 50 },
                                "& .MuiOutlinedInput-input": {
                                    paddingTop: 0.75,
                                    paddingBottom: 0.75,
                                },
                            }}
                        />
                    </Stack>
                )}
                <Stack>
                    <Tooltip
                        title="Choose the comparison used for the summary and percentage-based targets."
                        placement="top"
                        arrow
                    >
                        <Typography>Comparison</Typography>
                    </Tooltip>

                    <Select
                        value={comparison}
                        onChange={(e) => setComparison(e.target.value)}
                        sx={{ maxHeight: 50, minWidth: 180 }}
                        inputProps={{ "aria-label": "Comparison" }}
                    >
                        {[
                            COMPARISON_PREVIOUS_YEAR,
                            COMPARISON_SAME_TIME_LAST_YEAR,
                        ].map((key) => (
                            <MenuItem key={key} value={key}>
                                {COMPARISON_FILTER[key].label}
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
                <Stack>
                    <Tooltip
                        title={
                            "Choose how the Target line is generated: percentage, manual points, or a combination of both."
                        }
                        placement={"top"}
                        arrow
                    >
                        <Typography>Target mode</Typography>
                    </Tooltip>

                    <Select
                        value={targetMode}
                        onChange={(e) => {
                            setTargetMode(e.target.value);
                            setIsTargetEditMode(false);
                        }}
                        sx={{ maxHeight: 50, minWidth: 160 }}
                    >
                        <MenuItem value={TARGET_MODE_PERCENTAGE}>
                            Percentage
                        </MenuItem>
                        <MenuItem value={TARGET_MODE_MANUAL}>Manual</MenuItem>
                        <MenuItem value={TARGET_MODE_COMBINED}>
                            Combined
                        </MenuItem>
                    </Select>
                </Stack>
                {targetMode !== TARGET_MODE_MANUAL && (
                    <Stack>
                        <Tooltip
                            title={`Set your target growth % relative to ${comparisonLabel}.`}
                            placement={"top"}
                            arrow
                        >
                            <Typography>Target %</Typography>
                        </Tooltip>

                        <TextField
                            value={targetPct}
                            onChange={(e) => {
                                const raw = Number(e.target.value);
                                const val = Number.isFinite(raw) ? raw : 0;
                                const MAX = 200;
                                const clamped = Math.max(
                                    -1 * MAX,
                                    Math.min(MAX, val),
                                );
                                setTargetPct(clamped);
                            }}
                            inputProps={{
                                inputMode: "numeric",
                                pattern: "-?\\d*",
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        %
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            sx={{
                                maxWidth: 80,
                                minWidth: 60,
                                "& .MuiInputBase-root": { height: 50 },
                                "& .MuiOutlinedInput-input": {
                                    paddingTop: 0.75,
                                    paddingBottom: 0.75,
                                },
                                "& .MuiInputAdornment-root": {
                                    height: "100%",
                                    m: 0,
                                },
                            }}
                        />
                    </Stack>
                )}
                {targetMode !== TARGET_MODE_PERCENTAGE && (
                    <Stack sx={{ height: 50, justifyContent: "flex-end" }}>
                        <Button
                            variant={
                                isTargetEditMode ? "contained" : "outlined"
                            }
                            onClick={() => setIsTargetEditMode((v) => !v)}
                            // disabled={
                            //     isLoadingManualTargets || !userIdForTargets
                            // }
                            sx={{ height: 50 }}
                        >
                            {isTargetEditMode ? "Exit editing" : "Edit target"}
                        </Button>
                    </Stack>
                )}
                <Stack>
                    <Tooltip
                        title={
                            "Select the forecast confidence - higher confidence gives a wider range."
                        }
                        placement={"top"}
                        arrow
                    >
                        <Typography>Confidence Interval</Typography>
                    </Tooltip>
                    <Select
                        value={ciPct}
                        onChange={(e) => setCiPct(Number(e.target.value))}
                        sx={{ maxHeight: 50, minWidth: 100 }}
                    >
                        {[75, 80, 85, 90, 95].map((p) => (
                            <MenuItem key={p} value={p}>
                                {p}%
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
                <Stack sx={{ height: 50, justifyContent: "flex-end" }}>
                    <HotelSelector
                        compact
                        limitedIds={allowedHotelIds ?? undefined}
                    />
                </Stack>
                {isPredicting && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography>Predicting…</Typography>
                    </Stack>
                )}
            </Stack>

            {!isCurrentHotelTrained && !hasCachedForCurrent && (
                <div style={{ marginTop: 12 }}>
                    <Alert severity="warning">
                        <AlertTitle>Model coverage warning</AlertTitle>
                        This hotel is <b>not</b> in the trained-hotels list.
                    </Alert>
                </div>
            )}

            {!dismissNoPredictionsAlert &&
                hasCachedForCurrent &&
                !needsPredict &&
                !isPredicting &&
                !predictError &&
                (!hasPredictionForCurrent || !hasPredictionsForSelection) && (
                    <Alert
                        severity="warning"
                        sx={{ mt: 1.5 }}
                        onClose={() => setDismissNoPredictionsAlert(true)}
                    >
                        <AlertTitle>Pickup prediction unavailable</AlertTitle>
                        {!hasPredictionForCurrent
                            ? "Pickup predictions are not available for this hotel with the selected model and booking window."
                            : `No prediction data are available for the selected arrival period (${fromKey} - ${toKey}).`}
                    </Alert>
                )}

            {predictError && (
                <div style={{ marginTop: 12 }}>
                    <Alert
                        severity="error"
                        onClose={() => setPredictError(null)}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handlePredict}
                                disabled={isPredicting}
                            >
                                {isPredicting ? "Retrying…" : "Retry"}
                            </Button>
                        }
                    >
                        <AlertTitle>Prediction failed</AlertTitle>
                        {predictError}
                    </Alert>
                </div>
            )}

            <ReportTableMetricsSummary
                title="Period summary"
                data={summaryData}
                js={summaryJs}
                hideExtra={true}
                hideEvents={true}
                tooltipRenderers={{
                    [otbLabel]: () =>
                        `${metricLabel} already on the books for guests arriving during the selected period.`,

                    [predictedLabel]: () =>
                        `Expected total ${metricLabel.toLowerCase()} for the selected arrival period. Calculated by adding forecasted pickup to current OTB.`,

                    [pickupLabel]: () =>
                        metric === REVENUE
                            ? "Expected change in revenue. For each arrival date, forecasted room-night pickup is multiplied by the selected average daily rate (ADR), rounded, and then summed across the period."
                            : metric === BOOKINGS
                              ? "Expected additional bookings. For each arrival date, forecasted room-night pickup is divided by the estimated rooms per booking and rounded up. Negative daily estimates count as zero."
                              : "Expected change in room nights for the selected arrival period. Pickup equals predicted room nights minus room nights already on the books.",

                    [stlyLabel]: () =>
                        comparison === COMPARISON_SAME_TIME_LAST_YEAR
                            ? `${metricLabel} for the full equivalent arrival period last year, including only reservations made by this date last year. Includes cancellations.`
                            : `${metricLabel} for the full equivalent arrival period last year, without the same-time booking-date cutoff. Includes cancellations.`,
                    [targetLabel]: () =>
                        targetMode === TARGET_MODE_MANUAL
                            ? "Sum of the daily targets for the selected period. Values between manual entries are interpolated. Before the first entry and after the last, the nearest entered value is used."
                            : targetMode === TARGET_MODE_COMBINED
                              ? `Sum of the daily targets. Manual values apply on their dates; other dates use ${comparisonLabel} adjusted by the target percentage (${targetPct}%).`
                              : `Target ${metricLabel.toLowerCase()} = ${stlyLabel} x (1 + ${targetPct} / 100), rounded to the nearest whole number.`,

                    [varianceStlyLabel]: () =>
                        `Percentage above or below ${stlyLabel}. Calculated as ((OTB + Predicted) - ${comparisonLabel}) / ${comparisonLabel} x 100.`,

                    [varianceTargetLabel]: () =>
                        "Percentage above or below the target for the selected period. Calculated as ((OTB + Predicted) - Target) / Target x 100.",
                    [forecastCILabel]: () =>
                        metric === BOOKINGS
                            ? "Both ends show the predicted bookings total. A separate uncertainty range is currently unavailable for bookings."
                            : `Estimated range for total ${metricLabel.toLowerCase()} at the selected confidence level. The lower end is the sum of the daily lower estimates, and the upper end is the sum of the daily upper estimates.`,
                }}
                metrics={Object.fromEntries(
                    Object.entries(summaryData.group0.result).map(
                        ([name, value]) => [
                            name,
                            {
                                short: name,
                                format: !Number.isFinite(value)
                                    ? {}
                                    : name.includes("(%)")
                                      ? { right: "%", maxDecimals: 2 }
                                      : metric === REVENUE
                                        ? { left: "currency", maxDecimals: 2 }
                                        : { maxDecimals: 0 },
                                valueElement:
                                    Number.isFinite(value) &&
                                    (name === varianceStlyLabel ||
                                        name === varianceTargetLabel) ? (
                                        <SmallPercentage
                                            actual={100 + value}
                                            old={100}
                                            brackets={false}
                                            tooltip={false}
                                            bubble
                                        />
                                    ) : undefined,
                            },
                        ],
                    ),
                )}
            />

            <div style={{ height: 320, marginTop: 16 }}>
                {targetMode !== TARGET_MODE_PERCENTAGE && isTargetEditMode && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            mt: 1,
                            color: "text.secondary",
                        }}
                    >
                        Click on the chart to add/update a target point. Right
                        click an existing (big) point to delete it. (Alt+Click
                        also deletes.)
                    </Typography>
                )}
                <EChartsReact
                    ref={chartRef}
                    style={{ height: "100%" }}
                    option={chartOption}
                    notMerge={true}
                />
                <ConfirmModal
                    isOpen={isTargetConfirmOpen}
                    setIsOpen={(open) => {
                        setIsTargetConfirmOpen(open);
                        if (!open) setPendingTargetAction(null);
                    }}
                    text={
                        pendingTargetAction?.type === "delete"
                            ? "Delete target point?"
                            : "Save target point?"
                    }
                    subtext={
                        pendingTargetAction
                            ? `${pendingTargetAction.dateKey}: ${pendingTargetAction.value}`
                            : ""
                    }
                    acceptText={
                        pendingTargetAction?.type === "delete"
                            ? "Delete"
                            : "Save"
                    }
                    declineText="Cancel"
                    onAccept={() => {
                        const a = pendingTargetAction;
                        if (!a) return;

                        if (a.type === "delete") {
                            deleteManualTargetPoint(a.dateKey);
                        } else {
                            upsertManualTargetPoint(a.dateKey, a.value);
                        }

                        setPendingTargetAction(null);
                    }}
                    onDecline={() => setPendingTargetAction(null)}
                />
            </div>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => setIsEvaluateOpen(true)}
                    disabled={isLoadingModels}
                >
                    Evaluate mode
                </Button>
            </Stack>

            {/* Evaluate dialog: remembers past queries and lets you switch horizons/models */}
            <PickupEvaluateDialog
                open={isEvaluateOpen}
                onClose={() => setIsEvaluateOpen(false)}
                authToken={auth?.user?.jwt ?? ""}
                hotelId={hotelID}
                availableModelsMap={modelsMap}
                defaultModelName={modelName}
            />
        </Container>
    );
};

export default PickupScreen;
